import { memo, useMemo } from 'react';

import { GridColDef, GridRowId } from '@mui/x-data-grid-pro';

import { TipoControlePedidoMinimoAutoComplete } from 'components/autocompletes';
import { CissNumberField } from 'components/fields';
import { PanelGrid } from 'components/grid';
import { useClientContext } from 'context/ClientContext';
import { ConfigPedidoRemoveBtn } from 'pages/compra/configuracao-pedido/components';
import { useConfigPedidoListContext } from 'pages/compra/configuracao-pedido/contexts';
import { ConfigPedidoLojas } from 'pages/compra/configuracao-pedido/types';

type ConfigPedidoLojasGrid = {
    configPedidoLojas: ConfigPedidoLojas[];
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
};

/**
 * Transforma os produtos em linhas para o grid.
 */
const getRows = (products: ConfigPedidoLojas[]) =>
    products.map((item, index) => ({
        id: item.loja.idLoja,
        index,
        idLoja: item.loja.idLoja,
        cdLoja: item.loja.cdLoja,
        dsNomeFantasia: item.loja.dsNomeFantasia,
        dsRazaoSocial: item.loja.dsRazaoSocial,
        fgAtivo: item.fgAtivo,
        peRoyalties: item.peRoyalties,
        vlPedidoMinimo: item.vlPedidoMinimo,
        tipoControlePedidoMinimo: item.tipoControlePedidoMinimo,
    }));

type GridRows = ReturnType<typeof getRows>;

/**
 * Filtra os produtos com base no valor do filtro.
 */
const getFilteredRows = (lojas: GridRows, valueFilter: string) =>
    lojas.filter((loja) => loja.dsNomeFantasia.toLowerCase().includes(valueFilter.toLowerCase()) || loja.cdLoja.toString().includes(valueFilter));

export const ConfigPedidoLojasGrid = memo(({ configPedidoLojas, setFieldValue }: ConfigPedidoLojasGrid) => {
    const { valueFilter, setSelectedRows } = useConfigPedidoListContext();
    const { tipoPedido } = useClientContext();

    const rows = useMemo(() => {
        const rows = getRows(configPedidoLojas);

        return getFilteredRows(rows, valueFilter);
    }, [configPedidoLojas, valueFilter]);

    /**
     * Remove uma loja da lista de lojas.
     */
    const removeLoja = (idList: Set<GridRowId>) => {
        setFieldValue(
            'configPedidoLojas',
            configPedidoLojas.filter((loja) => !idList.has(loja.loja.idLoja)),
        );
    };

    const columns = useMemo((): GridColDef[] => {
        const columnsDef: GridColDef[] = [
            { field: 'cdLoja', headerName: 'Código', minWidth: 70 },
            { field: 'dsNomeFantasia', headerName: 'Nome da Loja', type: 'string', minWidth: 100, flex: 1 },
            { field: 'dsRazaoSocial', headerName: 'Razão Social', type: 'string', minWidth: 110, flex: 1 },
            {
                field: 'remover',
                align: 'center',
                headerAlign: 'center',
                headerName: 'Remover',
                filterable: false,
                sortable: false,
                hideable: false,
                disableColumnMenu: true,
                minWidth: 80,
                renderCell: ({ row }) => <ConfigPedidoRemoveBtn onClick={() => removeLoja(new Set([row.idLoja]))} />,
            },
        ];

        if (tipoPedido === 'padrao') {
            columnsDef.splice(
                3,
                0,
                {
                    field: 'peRoyalties',
                    headerName: 'Coeficiente ND (%)',
                    minWidth: 90,
                    renderCell: ({ row }) => (
                        <CissNumberField
                            size="small"
                            inputType="decimal"
                            maxValue={100}
                            forceMaxValue
                            preventEmptyField={false}
                            value={row.peRoyalties}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value);

                                setFieldValue(`configPedidoLojas.${row.index}.peRoyalties`, Number.isNaN(val) ? null : val);
                            }}
                        />
                    ),
                },
                {
                    field: 'tipoControlePedidoMinimo',
                    headerName: 'Controle de Mínimo',
                    minWidth: 180,
                    renderCell: ({ row }) => (
                        <TipoControlePedidoMinimoAutoComplete
                            size="small"
                            sx={{ width: '100%' }}
                            value={row.tipoControlePedidoMinimo}
                            onChange={(_e, value) => {
                                if (!value) {
                                    setFieldValue(`configPedidoLojas.${row.index}.vlPedidoMinimo`, 0);
                                }

                                setFieldValue(`configPedidoLojas.${row.index}.tipoControlePedidoMinimo`, value);
                            }}
                        />
                    ),
                },
                {
                    field: 'vlPedidoMinimo',
                    headerName: 'Mínimo',
                    minWidth: 80,
                    renderCell: ({ row }) => (
                        <CissNumberField
                            size="small"
                            inputType="decimal"
                            preventEmptyField={false}
                            value={row.vlPedidoMinimo}
                            disabled={!row.tipoControlePedidoMinimo}
                            onChange={(e) => {
                                const val = parseFloat(e.target.value);

                                setFieldValue(`configPedidoLojas.${row.index}.vlPedidoMinimo`, Number.isNaN(val) ? null : val);
                            }}
                        />
                    ),
                },
            );
        }

        return columnsDef;
    }, [configPedidoLojas]);

    return <PanelGrid rows={rows} reload={false} loading={false} columns={columns} remote={false} checkboxSelection onClickRow={setSelectedRows} />;
});
